import { useState, useEffect } from "react";
import * as axios from "axios";
import { SettingLinks } from "../premiumPaywallCheckout";
import PaywallCheckoutPersonalDetails from "./paywallCheckoutPersonalDetails"
import PaywallCheckoutPaymentDetails from "./paywallCheckoutPaymentDetails"
import PaywallCheckoutFinish from "./paywallCheckoutPaymentFinish"
import PaywallCheckoutMultiStepsProgressbar from "./multiStepProgressbar/paywallCheckoutMultiStepsProgressbar"
import { HyperLinkBuilder } from "../../../../commonComponents/inline/hyperLink";
import { getNewlinkTargetProperties } from "widgets/widgetsUtils"
import { useRedirect } from "../../../../../global/paywall/useRedirect";
import PremiumPaywallCheckoutPlans, { defaultPlansObj } from "../../../paywallForms/desktop/premiumPaywallPlans";
import { SubscriptionPlan, PaywallPlans, SiteLinks, CustomerAccessData } from "../../../../../global/paywall/paywallInterfaces";
import { AuthLogOutLocal } from "../../../../../global/paywall/authOperations";
import { LinkTargetProperties } from "../../../../../global/components/linkSettings/interfaces"
import { DetailsUpdateMode, DetailUpdateError, useDetailsUpdate } from "../../../../../global/paywall/useDetailsUpdate";
import PaywallRevalidate from "../../../paywallRevalidate/desktop/paywallRevalidate";
import { getPaywallAccessData } from "../../../../../global/paywall/paywallApiUrls";
import { hasProductClientId, ynetProductClientId } from "../../../../../global/paywall/paywallUtils";


declare const window: Window & {
    PAYWALL_PLANS: PaywallPlans
    PAYWALL_SITE_LINKS: SiteLinks;
}

enum CheckoutStep {
    SELECT_PLAN = "בחירת מסלול",
    PERSONAL_DETAILS = "פרטי מנוי",
    PAYMENT_DETAILS = "פרטי תשלום",
    FINISH = "סיום",
}

const targetLink: LinkTargetProperties = { ...getNewlinkTargetProperties(), linkOpenType: "new" }
const contactUrl = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.contact : '';
const termsUrl = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.terms : '';
const privacyUrl = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.privacy : '';
const authPageUrl = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.auth : '';
const myAccountUrl = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.myAccount : '';
const postPurchase = typeof window !== "undefined" && window.PAYWALL_SITE_LINKS ? window.PAYWALL_SITE_LINKS.postPurchase : '';

const settingLinks: SettingLinks = {
    terms: { url: termsUrl, target: targetLink },
    privacy: { url: privacyUrl, target: targetLink },
    authPage: { url: authPageUrl, target: targetLink },
    myAccount: { url: myAccountUrl, target: targetLink },
    contact: { url: contactUrl, target: targetLink },
    postPurchase: { url: postPurchase, target: targetLink }
};

const plansObj: PaywallPlans =
    typeof window !== 'undefined' && window.PAYWALL_PLANS
        ? window.PAYWALL_PLANS
        : defaultPlansObj;



export const PaywallCheckoutComponenta = () => {
    if (RUNNING_IN_SERVER) return null;
    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const selected = searchParams.get('selected');
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(selected ? 1 : 0);
    const [selectedPlan, setPlan] = useState<SubscriptionPlan>(null);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const { postPurchase, authPage, contact, privacy } = settingLinks;
    const [fallbackUrl, setFallbackUrl] = useState<string>(postPurchase.url);
    useRedirect({ fallbackUrl: fallbackUrl, enabled: shouldRedirect });
    const {
        customerDetails,
        setCustomerDetails,
        revalidationType,
        setRevalidationType,
        setAgreeToTermsOfUse,
        agreeToTermsOfUse,
        error,
        clearError,
        revalidateUser,
        revalidationStep,
        saveCustomerDetails,
        sendRevalidation,
        validateOtp
    } = useDetailsUpdate(DetailsUpdateMode.PURCHASE);

    const [hasCheckedAccess, setHasCheckedAccess] = useState(false);

    useEffect(() => {
        if (customerDetails.email && !hasCheckedAccess) {
            checkForAccess();
            setHasCheckedAccess(true);
        }
    }, [customerDetails, hasCheckedAccess]);


    useEffect(() => {
        if (typeof window === 'undefined') return;
        const searchParams = new URLSearchParams(window.location.search);
        const selected = searchParams.get('selected');
        setPlan(plansObj[selected === "YEARLY" || selected === "MONTHLY" ? selected : "MONTHLY"] || null);

    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.history.replaceState({ step: currentStepIndex }, "");
        const handlePopState = (event: PopStateEvent) => {
            const previousIndex = event.state && event.state.step;
            if (steps[currentStepIndex] === CheckoutStep.FINISH) {
                window.history.pushState({ step: currentStepIndex }, "");
                return;
            }
            if (previousIndex != null && previousIndex < currentStepIndex) {
                setCurrentStepIndex(previousIndex);
            } else {
                window.location.href = "/";
            }
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [currentStepIndex]);


    useEffect(() => {
        if (error === DetailUpdateError.NOT_LOGGED_IN) {
            setFallbackUrl(settingLinks.authPage.url);
            AuthLogOutLocal();
            setShouldRedirect(true);
        }
    }, [error]);


const checkForAccess = async () => {
    if (!customerDetails.email) {
        return;
    }
    try {
        const response = await axios.get<{ data: CustomerAccessData }>(getPaywallAccessData());
        const accessData = response && response.data && response.data.data as CustomerAccessData;
        const hasProducts = hasProductClientId(accessData.claims, ynetProductClientId);
        if (accessData && hasProducts) {
            setFallbackUrl(settingLinks.postPurchase.url);
            setShouldRedirect(true);
        } 
    } catch (error) {
        console.error("Error fetching customer access data:", error);
    }
}

    if (revalidateUser) {
        return <PaywallRevalidate
            userData={revalidateUser}
            step={revalidationStep}
            error={error}
            revalidationType={revalidationType}
            setRevalidationType={setRevalidationType}
            sendRevalidation={sendRevalidation}
            validateOtp={validateOtp}
        />;
    }

    const steps = Object.values(CheckoutStep);
    const nextStep = (plan?) => {
        if (plan) {
            setPlan(plansObj[plan]);
        }
        const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1);
        setCurrentStepIndex(nextIndex);
        typeof window !== "undefined" && window.history.pushState({ step: nextIndex }, "");
    };
    const currentStepClass = Object.keys(CheckoutStep)[currentStepIndex] || '';
    const renderStepContent = () => {
        switch (steps[currentStepIndex]) {
            case CheckoutStep.SELECT_PLAN:
                return <PremiumPaywallCheckoutPlans insideCheckoutFlow={true} nextStep={nextStep} />;
            case CheckoutStep.PERSONAL_DETAILS:
                return <PaywallCheckoutPersonalDetails
                    setCustomerDetails={setCustomerDetails}
                    nextStep={nextStep}
                    customerDetails={customerDetails}
                    settingLinks={settingLinks}
                    setAgreeToTermsOfUse={setAgreeToTermsOfUse}
                    agreeToTermsOfUse={agreeToTermsOfUse}
                    error={error}
                    clearError={clearError}
                    saveCustomerDetails={saveCustomerDetails}
                />;
            case CheckoutStep.PAYMENT_DETAILS:
                return <PaywallCheckoutPaymentDetails nextStep={nextStep} selectedPlan={selectedPlan} />;
            case CheckoutStep.FINISH:
                return <PaywallCheckoutFinish
                    settingLinks={settingLinks}
                    customerDetails={customerDetails}
                    selectedPlan={selectedPlan}
                    setShouldRedirect={setShouldRedirect}
                    nextStep={nextStep}
                />;
            default:
                return null;
        }
    };

    return (
        <div className={`PaywallCheckoutComponenta ${currentStepClass}`}>
            <div className="welcome-image-cover"></div>
            <div className="main-container">
                <div className="title-container">
                    <span className="title">רכישת מינוי </span>
                    <span className="yedioth-plus-logo"></span>
                </div>
                <PaywallCheckoutMultiStepsProgressbar steps={steps} currentStepIndex={currentStepIndex} />
                <div className="line-divider"></div>
                <div className="step-content-container">
                    {renderStepContent()}
                </div>
                <div className="footer">
                    <div className="footer-links">
                        <ul>
                            <li><HyperLinkBuilder linkTarget={contact.target} href={contact.url}>צור קשר</HyperLinkBuilder></li>
                            <li>|</li>
                            <li><HyperLinkBuilder linkTarget={privacy.target} href={privacy.url}> מדיניות פרטיות</HyperLinkBuilder></li>
                        </ul>
                    </div>
                    <div className="footer-terms-text">
                        לביטול מינוי ידיעות+ כדין יש לשלוח שם מלא וטלפון באחד מהאופנים הבאים:  <a target="_blank" href={contact.url}>מילוי טופס</a> או בדוא״ל <a href="mailto:support.plus@yedioth.co.il">support.plus@yedioth.co.il</a>  או בת.ד 438 ראשון לציון או בטל׳  3733* / 03-6933933 או בפקס 03-6933999 | ידיעות מינויים ח.פ 540181054
                    </div>
                </div>
            </div>

        </div>
    )
}

PaywallCheckoutComponenta.siteScriptName = "PaywallCheckoutComponenta"
